// Nachrichten: Meldungen aus Region, Deutschland und Welt für die Moderation, Unwetterwarnungen,
// eigene Nachrichtenblöcke und – für Admins – die Quellen des Radios.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, postsApi, type NewsArea, type NewsBlock, type NewsItem, type NewsSettings, type NewsWarning, type Verkehrsmeldung, type WarnCell, type Wetterlage } from "../api";
import { useAuth } from "../auth/AuthProvider";
import { errorText } from "../components/streaming-ui";
import { Badge, EmptyState, Page, PageHeader, Skeleton, Tabs } from "../components/ui";

const AREA_LABEL: Record<NewsArea, string> = { region: "Region", germany: "Deutschland", world: "Welt" };
const AREAS: NewsArea[] = ["region", "germany", "world"];

const clock = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return today
    ? d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleString("de-DE", { weekday: "short", hour: "2-digit", minute: "2-digit" });
};

export function NewsPage(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === "tenant_admin" || user?.role === "super_admin";
  const canBroadcast = isAdmin || user?.role === "moderator";
  const [tab, setTab] = useState<"news" | "blocks" | "sources">("news");

  return (
    <Page className="max-w-5xl">
      <PageHeader
        eyebrow="Programm"
        title="Nachrichten"
        description={<>
          Meldungen aus eurer Region, Deutschland und der Welt – dazu Unwetterwarnungen des Deutschen Wetterdiensts.
          Auch im Studio im Panel „Nachrichten“.
        </>}
      >
        <div className="-mx-1 overflow-x-auto px-1">
          <Tabs
            value={tab}
            onChange={setTab}
            items={([["news", "Meldungen"], ["blocks", "Eigene Nachrichten"], ...(isAdmin ? [["sources", "Quellen & Region"]] : [])] as Array<[typeof tab, string]>).map(([id, label]) => ({ value: id, label }))}
            className="whitespace-nowrap"
          />
        </div>
      </PageHeader>
      <div className="animate-fade-in">
        {tab === "news" && <NewsFeed canRefresh={canBroadcast} onSetup={isAdmin ? () => setTab("sources") : undefined} />}
        {tab === "blocks" && <NewsBlocks canUpload={canBroadcast} />}
        {tab === "sources" && isAdmin && <NewsSourcesPanel />}
      </div>
    </Page>
  );
}

function LegalHint(): JSX.Element {
  return (
    <div className="callout callout-warning block">
      <b>Nur zur Information für die Moderation.</b> Nicht wörtlich vorlesen – in eigenen Worten formulieren.
      Pressemitteilungen von Polizei und Behörden dürfen verwendet werden.
    </div>
  );
}

export function WarningList({ warnings }: { warnings: NewsWarning[] }): JSX.Element | null {
  if (warnings.length === 0) return null;
  return (
    <div className="space-y-2">
      {warnings.map((w) => (
        <div key={w.id} className="card overflow-hidden border-l-4 p-4" style={{ borderLeftColor: w.color }}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full px-2.5 py-0.5 text-xs font-bold text-black" style={{ background: w.color }}>{w.level_label}</span>
            <span className="font-semibold text-primary">{w.headline ?? w.event}</span>
          </div>
          <div className="mt-1 text-xs text-muted">
            {w.region_name} · {w.starts_at ? `ab ${clock(w.starts_at)}` : ""}{w.ends_at ? ` bis ${clock(w.ends_at)}` : ""}
          </div>
          {w.description && <p className="mt-1 text-sm text-secondary">{w.description}</p>}
          {w.instruction && <p className="mt-1 text-sm text-secondary"><b>Hinweis:</b> {w.instruction}</p>}
          <div className="mt-1 text-[11px] text-muted">Quelle: Deutscher Wetterdienst</div>
        </div>
      ))}
    </div>
  );
}

function NewsFeed({ canRefresh, onSetup }: { canRefresh: boolean; onSetup?: () => void }): JSX.Element {
  const [area, setArea] = useState<NewsArea>("region");
  const [items, setItems] = useState<NewsItem[] | null>(null);
  const [warnings, setWarnings] = useState<NewsWarning[]>([]);
  const [settings, setSettings] = useState<NewsSettings | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [n, w, s] = await Promise.all([api.listNews(undefined, 300), api.listWarnings(), api.getNewsSettings()]);
      setItems(n);
      setWarnings(w);
      setSettings(s);
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }, []);
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 60_000);
    return () => clearInterval(t);
  }, [load]);

  const counts = useMemo(() => Object.fromEntries(AREAS.map((a) => [a, (items ?? []).filter((i) => i.area === a).length])), [items]);
  const shown = (items ?? []).filter((i) => i.area === area);

  return (
    <div className="space-y-3">
      <LegalHint />
      {error && <div className="callout callout-danger">{error}</div>}
      {settings && settings.sources.length === 0 && (
        <div className="callout callout-info block">
          Für dieses Radio sind noch keine Nachrichten-Quellen eingerichtet.
          {onSetup ? <> <button onClick={onSetup} className="font-medium underline">Region wählen</button>, dann schlägt die Suite passende Feeds vor.</> : " Ein Admin kann unter „Quellen & Region“ die Region wählen."}
        </div>
      )}
      <WarningList warnings={warnings} />
      <div className="flex flex-wrap items-center gap-2">
        <div className="tabbar" role="tablist">
          {AREAS.map((a) => (
            <button key={a} onClick={() => setArea(a)} role="tab" aria-selected={area === a}
              className={`tab ${area === a ? "tab-active" : ""}`}>
              {AREA_LABEL[a]} <span className="ml-1 text-xs tabular-nums opacity-70">({counts[a] ?? 0})</span>
            </button>
          ))}
        </div>
        <span className="flex-1" />
        {canRefresh && (
          <button disabled={busy} onClick={() => {
            setBusy(true);
            api.refreshNews().then(load).catch((err) => setError(errorText(err))).finally(() => setBusy(false));
          }} className="btn btn-secondary">{busy ? "Rufe ab …" : "Jetzt abrufen"}</button>
        )}
      </div>
      {items === null ? (
        <div className="card space-y-3 p-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-5" />)}</div>
      ) : shown.length === 0 ? (
        <EmptyState title={`Keine Meldungen in „${AREA_LABEL[area]}“.`} />
      ) : (
        <ul className="card divide-y divide-line overflow-hidden">
          {shown.map((n) => (
            <li key={n.id} className={`px-4 py-2.5 transition-colors ${open === n.id ? "bg-surface-sunken" : "hover:bg-surface-hover"}`}>
              <button onClick={() => setOpen(open === n.id ? null : n.id)} className="flex w-full items-baseline gap-3 text-left">
                <span className="w-24 shrink-0 font-mono text-xs tabular-nums text-muted">{clock(n.at)}</span>
                <span className={`min-w-0 flex-1 text-sm text-primary ${open === n.id ? "font-medium" : ""}`}>{n.title}</span>
                {n.source && <span className="hidden shrink-0 sm:inline"><Badge>{n.source}</Badge></span>}
              </button>
              {open === n.id && (
                <div className="mt-2 space-y-2 pb-1 text-sm text-secondary sm:pl-[6.75rem]">
                  {n.summary && <p>{n.summary}</p>}
                  <div className="text-xs text-muted">
                    {n.source}{n.link && <> · <a href={n.link} target="_blank" rel="noreferrer noopener" className="underline">Quelle öffnen</a></>}
                  </div>
                  <DraftFromNews newsId={n.id} onError={setError} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Aus einer Meldung einen Entwurf machen.
 *
 * Es wandert nur ein Sprechzettel in den Entwurf: Stichpunkte und die Quelle.
 * Der fremde Wortlaut ist urheberrechtlich geschützt, die Nachricht selbst nicht.
 */
function DraftFromNews({ newsId, onError }: { newsId: string; onError: (msg: string) => void }): JSX.Element {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  return (
    <div className="pt-1">
      <button disabled={busy} className="btn btn-secondary btn-sm"
        onClick={() => {
          setBusy(true);
          postsApi.fromNews(newsId)
            .then((res) => {
              setNote(res.message);
              navigate("/redaktion");
            })
            .catch((err) => onError(errorText(err)))
            .finally(() => setBusy(false));
        }}>
        {busy ? "Lege an …" : "Als Beitrag entwerfen"}
      </button>
      <p className="mt-1 text-xs text-muted">
        {note ?? "Legt einen Entwurf mit Stichpunkten und Quelle an. Bitte in eigenen Worten schreiben."}
      </p>
    </div>
  );
}

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function NewsBlocks({ canUpload }: { canUpload: boolean }): JSX.Element {
  const [blocks, setBlocks] = useState<NewsBlock[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [validUntil, setValidUntil] = useState(() => {
    const d = new Date();
    d.setMinutes(0, 0, 0);
    d.setHours(d.getHours() + 2);
    return toLocalInput(new Date(d.getTime() - 60_000));
  });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    api.listNewsBlocks().then(setBlocks).catch((err) => setError(errorText(err)));
  }, []);
  useEffect(load, [load]);

  const upload = async (file: File): Promise<void> => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.uploadNewsBlock(file, title, new Date(validUntil).toISOString());
      setNotice(res.plays_at
        ? `Hochgeladen. Läuft um ${new Date(res.plays_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr im Nachrichten-Platz der Stundenuhr.`
        : "Hochgeladen – aber gültig nur bis vor der nächsten vollen Stunde, deshalb wird er nicht automatisch gespielt.");
      setTitle("");
      load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="max-w-3xl text-sm leading-relaxed text-muted">
        Eigene Nachrichten, in eigenen Worten gesprochen, laufen zur vollen Stunde im Platz „Nachrichten“ der Stundenuhr –
        solange sie gültig sind. Aufnehmen geht am einfachsten im Studio (Panel „Nachrichten“ → „Nachrichten vorbereiten“),
        fertige Dateien lassen sich hier hochladen.
      </p>
      {error && <div className="callout callout-danger">{error}</div>}
      {notice && <div className="callout callout-success">{notice}</div>}
      {canUpload && (
        <div className="card flex flex-wrap items-end gap-3 p-4">
          <label className="flex min-w-0 flex-1 basis-56 flex-col text-[0.8rem] font-medium text-secondary">
            Titel (freiwillig)
            <input id="news-block-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="z. B. Nachrichten 14 Uhr" maxLength={200}
              className="input mt-1.5 font-normal" />
          </label>
          <label className="flex flex-col text-[0.8rem] font-medium text-secondary">
            Gültig bis
            <input id="news-block-valid" type="datetime-local" value={validUntil} onChange={(e) => setValidUntil(e.target.value)}
              className="input mt-1.5 font-normal" />
          </label>
          <button disabled={busy} onClick={() => fileRef.current?.click()} className="btn btn-primary">
            {busy ? "Lade hoch …" : "Datei hochladen"}
          </button>
          <input ref={fileRef} type="file" accept="audio/*" hidden onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) void upload(f);
          }} />
        </div>
      )}
      {blocks === null ? <div className="card space-y-3 p-4">{[0, 1].map((i) => <Skeleton key={i} className="h-5" />)}</div> : blocks.length === 0 ? (
        <EmptyState title="Noch keine eigenen Nachrichten in den letzten drei Tagen." />
      ) : (
        <ul className="card divide-y divide-line overflow-hidden">
          {blocks.map((b) => (
            <li key={b.id} className="flex flex-wrap items-center gap-2 px-4 py-2.5 text-sm hover:bg-surface-hover">
              <span className="min-w-0 flex-1 truncate font-medium text-primary">{b.title}</span>
              <span className="text-xs text-muted">{b.artist}</span>
              {b.planned_at ? (
                <span className="badge badge-success">läuft um {new Date(b.planned_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</span>
              ) : b.expired ? (
                <span className="badge badge-neutral">abgelaufen</span>
              ) : (
                <span className="badge badge-warning">gültig bis {clock(b.valid_until)}</span>
              )}
              {b.last_played_at && <span className="text-xs tabular-nums text-muted">gelaufen {clock(b.last_played_at)}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * „50.196, 6.831“ → Mitte des Sendegebiets.
 *
 * Absichtlich ein Feld statt zwei: Aus jeder Karte kommt das Paar in einem
 * Stück, und wer es teilt, vertauscht irgendwann Breite und Länge.
 */
function ortLesen(eingabe: string): { lat: number | null; lon: number | null } {
  const teile = eingabe.split(/[,;\s]+/).filter((t) => t !== "");
  const lat = Number(teile[0]);
  const lon = Number(teile[1]);
  if (teile.length < 2 || !Number.isFinite(lat) || !Number.isFinite(lon)) return { lat: null, lon: null };
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return { lat: null, lon: null };
  return { lat, lon };
}

function NewsSourcesPanel(): JSX.Element {
  const [settings, setSettings] = useState<NewsSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [land, setLand] = useState("");
  const [cells, setCells] = useState<WarnCell[]>([]);
  const [search, setSearch] = useState("");
  const [hits, setHits] = useState<WarnCell[]>([]);
  const [places, setPlaces] = useState("");
  const [autobahnen, setAutobahnen] = useState("");
  const [umkreisOrt, setUmkreisOrt] = useState("");
  const [umkreisKm, setUmkreisKm] = useState("");
  const [verkehr, setVerkehr] = useState<Verkehrsmeldung[]>([]);
  const [wetterAn, setWetterAn] = useState(false);
  const [wetter, setWetter] = useState<Wetterlage | null>(null);
  const [custom, setCustom] = useState({ area: "region" as NewsArea, name: "", url: "" });
  const [test, setTest] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const s = await api.getNewsSettings();
      setSettings(s);
      setLand((l) => l || s.land || "");
      setCells(s.warncells);
      setAutobahnen(s.autobahnen.join(", "));
      setUmkreisOrt(s.verkehr_ort ? `${s.verkehr_ort.lat}, ${s.verkehr_ort.lon}` : "");
      setUmkreisKm(s.verkehr_umkreis > 0 ? String(s.verkehr_umkreis) : "");
      // Die laufenden Meldungen nebenher: Wer die Autobahnen einstellt, will
      // sofort sehen, ob etwas ankommt – und nicht erst auf der Hörerseite.
      setWetterAn(s.wetter);
      api.getVerkehr().then((v) => setVerkehr(v.entries)).catch(() => setVerkehr([]));
      api.getWetter().then((w) => setWetter(w.wetter)).catch(() => setWetter(null));
    } catch (err) {
      setError(errorText(err));
    }
  }, []);
  useEffect(() => void load(), [load]);

  useEffect(() => {
    if (search.trim().length < 2) {
      setHits([]);
      return;
    }
    const t = setTimeout(() => {
      api.searchNewsRegions(search, land || undefined).then(setHits).catch(() => setHits([]));
    }, 250);
    return () => clearTimeout(t);
  }, [search, land]);

  const act = async (fn: () => Promise<unknown>, ok?: string): Promise<void> => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (ok) setNotice(ok);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return error ? <div className="callout callout-danger">{error}</div> : <div className="card space-y-3 p-5"><Skeleton className="h-5 w-40" /><Skeleton className="h-9" /></div>;
  const firstTime = settings.sources.length === 0;

  return (
    <div className="space-y-4">
      {error && <div className="callout callout-danger">{error}</div>}
      {notice && <div className="callout callout-success">{notice}</div>}

      <section className="space-y-3 card p-5">
        <h2 className="card-title">Region</h2>
        <p className="text-sm text-muted">
          Bundesland und Landkreise bestimmen die Unwetterwarnungen und die Vorschläge für regionale Nachrichten.
        </p>
        <div className="flex flex-wrap gap-3">
          <label className="flex flex-col text-[0.8rem] font-medium text-secondary">
            Bundesland
            <select id="news-land" value={land} onChange={(e) => setLand(e.target.value)} className="input mt-1.5 font-normal">
              <option value="">– wählen –</option>
              {Object.entries(settings.laender).map(([code, name]) => <option key={code} value={code}>{name}</option>)}
            </select>
          </label>
          <label className="relative flex min-w-0 flex-1 basis-64 flex-col text-[0.8rem] font-medium text-secondary">
            Landkreis oder kreisfreie Stadt hinzufügen
            <input id="news-kreis-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name eintippen, z. B. Vulkaneifel"
              className="input mt-1.5 font-normal" />
            {hits.length > 0 && (
              <ul className="absolute top-full z-10 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-line bg-card py-1 shadow-lg">
                {hits.map((h) => (
                  <li key={h.id}>
                    <button onClick={() => {
                      if (!cells.some((c) => c.id === h.id)) setCells([...cells, h]);
                      if (!land) setLand(h.land);
                      setSearch("");
                      setHits([]);
                    }} className="w-full px-3 py-1.5 text-left text-sm font-normal text-primary hover:bg-surface-hover">{h.name}</button>
                  </li>
                ))}
              </ul>
            )}
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          {cells.length === 0 && <span className="text-sm text-muted">Noch kein Landkreis gewählt.</span>}
          {cells.map((c) => (
            <span key={c.id} className="flex items-center gap-1.5 rounded-full bg-brand-primary/10 py-1 pl-3 pr-2 text-sm font-medium text-brand-primary">
              {c.name}
              <button onClick={() => setCells(cells.filter((x) => x.id !== c.id))} aria-label={`${c.name} entfernen`} className="flex h-4 w-4 items-center justify-center rounded-full text-xs opacity-70 hover:bg-brand-primary/20 hover:opacity-100">✕</button>
            </span>
          ))}
        </div>
        <label className="flex flex-col text-[0.8rem] font-medium text-secondary">
          Orte für Polizei- und Behördenmeldungen (presseportal.de, mit Komma getrennt)
          <input id="news-places" value={places} onChange={(e) => setPlaces(e.target.value)} placeholder="z. B. Daun, Gerolstein, Bitburg, Prüm"
            className="input mt-1.5 font-normal" />
          <span className="field-hint font-normal">Nach Orten findet presseportal.de zuverlässiger als nach Landkreisen.</span>
        </label>
        <button disabled={busy || !land} onClick={() => void act(
          () => api.saveNewsRegion({ land, warncells: cells.map((c) => c.id), add_suggestions: true, places: places.split(",").map((p) => p.trim()).filter((p) => p.length >= 2) }),
          firstTime ? "Region gespeichert und passende Quellen angelegt." : "Region gespeichert. Fehlende Vorschläge wurden ergänzt."
        ).then(() => setPlaces(""))} className="btn btn-primary">
          Region speichern {firstTime ? "und Quellen anlegen" : ""}
        </button>
      </section>

      <section className="space-y-3 card p-5">
        <h2 className="card-title">Verkehr und Wetter</h2>
        <p className="text-sm text-muted">
          Staus, Sperrungen und Baustellen auf euren Autobahnen – von der Autobahn GmbH, alle paar Minuten neu.
          Auf die Hörerseite kommen sie über den Baustein „Verkehr“ oder als Zeichen in der Servicezeile.
        </p>
        <label className="flex flex-col text-[0.8rem] font-medium text-secondary">
          Autobahnen (mit Komma getrennt)
          <input id="news-autobahnen" value={autobahnen} onChange={(e) => setAutobahnen(e.target.value)} placeholder="z. B. A1, A48, A61"
            className="input mt-1.5 font-normal" />
          <span className="field-hint font-normal">Leer lassen heißt: kein Verkehr. Höchstens zwölf.</span>
        </label>
        <div className="flex flex-wrap gap-3">
          <label className="flex flex-col text-[0.8rem] font-medium text-secondary">
            Mitte eures Sendegebiets (Breite, Länge)
            <input id="news-umkreis-ort" value={umkreisOrt} onChange={(e) => setUmkreisOrt(e.target.value)} placeholder="z. B. 50.196, 6.831"
              className="input mt-1.5 font-normal" />
            <span className="field-hint font-normal">In einer Karte auf euren Ort rechtsklicken – die zwei Zahlen stehen dort.</span>
          </label>
          <label className="flex flex-col text-[0.8rem] font-medium text-secondary">
            Umkreis in km
            <input id="news-umkreis-km" type="number" min={0} max={500} value={umkreisKm} onChange={(e) => setUmkreisKm(e.target.value)} placeholder="80"
              className="input mt-1.5 w-28 font-normal" />
            <span className="field-hint font-normal">Leer oder 0: alles, was auf der Autobahn los ist – auch am anderen Ende.</span>
          </label>
        </div>
        <label className="flex items-start gap-2 text-sm text-secondary">
          <input type="checkbox" checked={wetterAn} onChange={(e) => setWetterAn(e.target.checked)} className="mt-0.5" />
          <span>
            <b>Wetter am Standort zeigen</b> – wie es gerade ist und die nächsten drei Tage, vom Deutschen
            Wetterdienst. Auf die Hörerseite kommt es über den Baustein „Wetter“ oder als Zahl in der
            Servicezeile. Ohne Standort oben geht es nicht.
          </span>
        </label>
        <button disabled={busy} onClick={() => void act(
          () => api.saveAutobahnen({ autobahnen, ...ortLesen(umkreisOrt), umkreis: Number(umkreisKm) || 0, wetter: wetterAn }),
          "Verkehr und Wetter gespeichert."
        )} className="btn btn-primary">
          Speichern
        </button>
        {wetterAn && wetter && (
          <div className="well text-sm text-primary">
            <span className="section-label">Zurzeit</span>
            <div>{wetter.zeile}{wetter.tage[0] ? ` · heute ${wetter.tage[0].max ?? "–"}°/${wetter.tage[0].min ?? "–"}°` : ""}</div>
          </div>
        )}
        {settings.autobahnen.length > 0 && (
          <div>
            <div className="section-label mt-2">Zurzeit gemeldet</div>
            {verkehr.length === 0 && <div className="text-sm text-muted">Nichts los – freie Fahrt.</div>}
            <ul className="mt-1 space-y-1">
              {verkehr.slice(0, 12).map((m, i) => (
                <li key={i} className={`text-sm ${m.gesperrt ? "font-semibold text-red-700" : "text-primary"}`}>{m.zeile}</li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className="space-y-2 card p-5">
        <h2 className="card-title">Quellen</h2>
        {AREAS.map((a) => {
          const list = settings.sources.filter((s) => s.area === a);
          return (
            <div key={a}>
              <div className="section-label mb-1 mt-3">{AREA_LABEL[a]}</div>
              {list.length === 0 && <div className="text-sm text-muted">Keine Quelle.</div>}
              <ul>
                {list.map((s) => (
                  <li key={s.id} className="flex flex-wrap items-center gap-2 border-b border-line py-2 last:border-0">
                    <input id={`news-src-${s.id}`} type="checkbox" checked={s.enabled} onChange={(e) => void act(() => api.updateNewsSource(s.id, { enabled: e.target.checked }))} />
                    <label htmlFor={`news-src-${s.id}`} className="min-w-0 flex-1 text-sm text-primary">
                      {s.name} <span className="text-xs tabular-nums text-muted">· {s.items} Meldungen</span>
                    </label>
                    {s.last_error ? (
                      <span className="badge badge-danger max-w-full" title={s.url}>{s.last_error}</span>
                    ) : s.last_ok_at ? (
                      <span className="badge badge-success">abgerufen {clock(s.last_ok_at)}</span>
                    ) : null}
                    <button onClick={() => window.confirm(`„${s.name}“ entfernen? Ihre Meldungen verschwinden auch.`) && void act(() => api.deleteNewsSource(s.id))}
                      className="btn btn-ghost btn-sm text-red-600 dark:text-red-400">Entfernen</button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </section>

      {settings.suggestions.some((s) => !s.added) && (
        <section className="space-y-2 card p-5">
          <h2 className="card-title">Weitere Vorschläge</h2>
          <ul>
            {settings.suggestions.filter((s) => !s.added).map((s) => (
              <li key={s.key} className="flex flex-wrap items-center gap-2 border-b border-line py-2 text-sm last:border-0">
                <span className="badge badge-neutral">{AREA_LABEL[s.area]}</span>
                <span className="min-w-0 flex-1 text-primary">{s.name}</span>
                <button disabled={busy} onClick={() => void act(() => api.addNewsSource({ area: s.area, name: s.name, url: s.url, preset_key: s.key }), `„${s.name}“ hinzugefügt.`)}
                  className="btn btn-secondary btn-sm">Hinzufügen</button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="space-y-2 card p-5">
        <h2 className="card-title">Eigener Feed</h2>
        <p className="text-sm text-muted">Jeder RSS- oder Atom-Feed, z. B. eurer Stadt, Gemeinde oder Zeitung.</p>
        <div className="flex flex-wrap gap-2">
          <select id="news-custom-area" value={custom.area} onChange={(e) => setCustom({ ...custom, area: e.target.value as NewsArea })} className="input w-auto">
            {AREAS.map((a) => <option key={a} value={a}>{AREA_LABEL[a]}</option>)}
          </select>
          <input id="news-custom-name" value={custom.name} onChange={(e) => setCustom({ ...custom, name: e.target.value })} placeholder="Name" maxLength={80}
            className="input w-auto min-w-0 flex-1 basis-40" />
          <input id="news-custom-url" value={custom.url} onChange={(e) => setCustom({ ...custom, url: e.target.value })} placeholder="https://…/feed.xml" maxLength={500}
            className="input min-w-0 flex-[2] basis-64" />
        </div>
        <div className="flex flex-wrap gap-2">
          <button disabled={busy || !custom.url} onClick={() => {
            setTest(null);
            void act(async () => {
              const r = await api.testNewsSource(custom.url);
              setTest(`${r.title ?? "Feed"}: ${r.count} Meldungen, z. B. „${r.items[0]?.title ?? ""}“`);
              if (!custom.name && r.title) setCustom((c) => ({ ...c, name: r.title!.slice(0, 80) }));
            });
          }} className="btn btn-secondary">Testen</button>
          <button disabled={busy || !custom.url || !custom.name} onClick={() => void act(async () => {
            await api.addNewsSource({ area: custom.area, name: custom.name, url: custom.url });
            setCustom({ area: custom.area, name: "", url: "" });
            setTest(null);
          }, "Feed hinzugefügt. Die ersten Meldungen kommen beim nächsten Abruf.")} className="btn btn-primary">Hinzufügen</button>
        </div>
        {test && <div className="callout callout-success">{test}</div>}
      </section>
      <p className="text-xs text-muted">
        Unwetterwarnungen: Deutscher Wetterdienst (offene Daten). Die Suite ruft Feeds alle 10 Minuten ab und hebt Meldungen drei Tage auf.
      </p>
    </div>
  );
}
